import type { BooksyAssistantDiagnostic } from '../types/chat-message'
import {
  USER_FACING_ERROR_KEYS,
  type BooksyAssistantErrorContext,
  type UserFacingAgentErrorKey,
} from '../types/errors'
import { AGENT_ERROR_CODES, type AgentErrorCode } from '../consts/agent-errors'

export const RATE_LIMIT_MARKER = 'RATE_LIMIT:'

export const RATE_LIMIT_MESSAGE_KEY = 'booksy_assistant.error.description.rate_limit'

export const MESSAGE_TOO_LONG_MARKER = 'MESSAGE_TOO_LONG:'

export const MESSAGE_TOO_LONG_MESSAGE_KEY = 'booksy_assistant.error.description.message_too_long'

export const CONVERSATION_TURN_LIMIT_MARKER = 'CONVERSATION_TURN_LIMIT:'

export const CONVERSATION_TURN_LIMIT_MESSAGE_KEY = 'booksy_assistant.error.description.conversation_turn_limit'

const AGENT_ERROR_MARKER_PREFIX = '__BOOKSY_ASSISTANT_ERROR__:'

export const AGENT_ERROR_MESSAGE_KEYS: Record<AgentErrorCode, string> = {
  [AGENT_ERROR_CODES.ACCESS_DENIED]: 'booksy_assistant.error.description.access_denied',
  [AGENT_ERROR_CODES.UPSTREAM_UNAVAILABLE]: 'booksy_assistant.error.description.upstream_unavailable',
  [AGENT_ERROR_CODES.AGENT_UNREACHABLE]: 'booksy_assistant.error.description.agent_unreachable',
  [AGENT_ERROR_CODES.REQUEST_FAILED]: 'booksy_assistant.error.description.request_failed',
  [AGENT_ERROR_CODES.STREAM_TIMEOUT]: 'booksy_assistant.error.description.stream_timeout',
  [AGENT_ERROR_CODES.EMPTY_STREAM]: 'booksy_assistant.error.description.empty_stream',
  [AGENT_ERROR_CODES.INVALID_RESPONSE]: 'booksy_assistant.error.description.invalid_response',
  [AGENT_ERROR_CODES.LOGIN_REQUIRED]: 'booksy_assistant.error.description.login_required',
}

export const LOGIN_REQUIRED_ERROR = createAgentError(AGENT_ERROR_CODES.LOGIN_REQUIRED)

export function createAgentError (code: AgentErrorCode, detail?: string): Error {
  const message = `${AGENT_ERROR_MARKER_PREFIX}${code}`
  return detail ? new Error(`${message}:${detail}`) : new Error(message)
}

export function resolveAgentErrorCode (error: unknown): AgentErrorCode | null {
  if (!(error instanceof Error)) {
    return null
  }

  if (!error.message.startsWith(AGENT_ERROR_MARKER_PREFIX)) {
    return null
  }

  const payload = error.message.slice(AGENT_ERROR_MARKER_PREFIX.length)
  const code = payload.split(':')[0] as AgentErrorCode

  if (code in AGENT_ERROR_MESSAGE_KEYS) {
    return code
  }

  return null
}

export function extractTechnicalErrorMessage (error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  return String(error)
}

export function buildBooksyAssistantDiagnostic (
  context: BooksyAssistantErrorContext,
  error: unknown
): BooksyAssistantDiagnostic {
  return {
    operation: context.operation,
    technicalMessage: extractTechnicalErrorMessage(error),
    occurredAt: new Date().toISOString(),
    ...(context.sessionId !== undefined ? { sessionId: context.sessionId } : {}),
    ...(context.userId !== undefined ? { userId: context.userId } : {}),
    ...(context.userMessage ? { userMessage: context.userMessage } : {}),
    ...(context.status !== undefined ? { status: context.status } : {}),
    ...(context.responseBody ? { responseBody: context.responseBody } : {}),
  }
}

export function formatBooksyAssistantDiagnostic (
  diagnostic: BooksyAssistantDiagnostic
): string {
  const lines = [
    `operation: ${diagnostic.operation}`,
    `time: ${diagnostic.occurredAt}`,
    `message: ${diagnostic.technicalMessage}`,
  ]

  if (diagnostic.sessionId) {
    lines.push(`sessionId: ${diagnostic.sessionId}`)
  }

  if (diagnostic.userId !== undefined) {
    lines.push(`userId: ${diagnostic.userId}`)
  }

  if (diagnostic.userMessage) {
    lines.push(`userMessage: ${diagnostic.userMessage}`)
  }

  if (diagnostic.status !== undefined) {
    lines.push(`status: ${diagnostic.status}`)
  }

  if (diagnostic.responseBody) {
    lines.push(`responseBody: ${diagnostic.responseBody}`)
  }

  return lines.join('\n')
}

export function logBooksyAssistantError (
  context: BooksyAssistantErrorContext,
  error: unknown
): BooksyAssistantDiagnostic {
  const diagnostic = buildBooksyAssistantDiagnostic(context, error)

  console.error(
    '[Booksy Assistant] Request failed — see details below',
    {
      ...context,
      technicalMessage: diagnostic.technicalMessage,
      error,
    }
  )

  return diagnostic
}

function isUserActionError (message: string): boolean {
  return resolveAgentErrorCode(new Error(message)) === AGENT_ERROR_CODES.LOGIN_REQUIRED
    || /must be logged in/i.test(message)
}

export function formatAgentErrorResponse (status: number, body: string): string {
  const trimmed = body.trim()
  const isHtml = trimmed.startsWith('<!DOCTYPE html') || trimmed.startsWith('<html')

  if (isHtml) {
    if (status === 403) {
      return createAgentError(AGENT_ERROR_CODES.ACCESS_DENIED).message
    }

    if (status === 502 || status === 503 || /no healthy upstream/i.test(trimmed)) {
      return createAgentError(AGENT_ERROR_CODES.UPSTREAM_UNAVAILABLE).message
    }

    return createAgentError(AGENT_ERROR_CODES.AGENT_UNREACHABLE).message
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      detail?: string
      error?: string
      message?: string
    }
    const detail = parsed.detail ?? parsed.error ?? parsed.message
    if (detail) {
      return detail
    }
  } catch {
    // Not JSON — fall through to raw body.
  }

  if (trimmed) {
    return trimmed
  }

  return createAgentError(AGENT_ERROR_CODES.REQUEST_FAILED, String(status)).message
}

export function isSessionNotFoundError (error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /session not found/i.test(message)
}

export function isRateLimitError (error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes(RATE_LIMIT_MARKER)
}

export function isRateLimitResponseText (text: string): boolean {
  return text.includes(RATE_LIMIT_MARKER)
}

export function isMessageTooLongError (error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes(MESSAGE_TOO_LONG_MARKER)
}

export function isMessageTooLongResponseText (text: string): boolean {
  return text.includes(MESSAGE_TOO_LONG_MARKER)
}

export function isConversationTurnLimitError (error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes(CONVERSATION_TURN_LIMIT_MARKER)
}

export function isConversationTurnLimitResponseText (text: string): boolean {
  return text.includes(CONVERSATION_TURN_LIMIT_MARKER)
}

export function resolveUserFacingErrorKey (error: unknown): UserFacingAgentErrorKey {
  const agentErrorCode = resolveAgentErrorCode(error)
  if (agentErrorCode === AGENT_ERROR_CODES.LOGIN_REQUIRED) {
    return USER_FACING_ERROR_KEYS.LOGIN_REQUIRED
  }

  if (error instanceof Error && isUserActionError(error.message)) {
    return USER_FACING_ERROR_KEYS.LOGIN_REQUIRED
  }

  if (isRateLimitError(error)) {
    return USER_FACING_ERROR_KEYS.RATE_LIMIT
  }

  if (isMessageTooLongError(error)) {
    return USER_FACING_ERROR_KEYS.MESSAGE_TOO_LONG
  }

  if (isConversationTurnLimitError(error)) {
    return USER_FACING_ERROR_KEYS.CONVERSATION_TURN_LIMIT
  }

  if (agentErrorCode) {
    return USER_FACING_ERROR_KEYS.AGENT_ERROR
  }

  return USER_FACING_ERROR_KEYS.GENERIC
}

export function resolveAgentErrorMessageKey (error: unknown): string | null {
  const agentErrorCode = resolveAgentErrorCode(error)
  if (!agentErrorCode) {
    return null
  }

  return AGENT_ERROR_MESSAGE_KEYS[agentErrorCode]
}
